import {
  Grid2x2,
  Swords,
  Target,
  Crosshair,
  FileText,
  GitBranch,
  ClipboardCheck,
  Scale,
  Briefcase,
  Calculator,
  Handshake,
  Library,
  PieChart,
  Move,
  Globe,
  Layers,
  ShieldCheck,
  Network,
} from "lucide-react";

const newId = (prefix) => `${prefix}-${Math.random().toString(36).slice(2, 8)}`;

export const strategyCards = [
  {
    id: "swot",
    title: "SWOT 2×2",
    eyebrow: "Single-screen",
    description: "Type Strengths, Weaknesses, Opportunities, and Threats. Export a polished 2×2 matrix as PDF.",
    path: "/strategy/swot",
    action: "Build SWOT",
    Icon: Grid2x2,
  },
  {
    id: "five-forces",
    title: "Porter's Five Forces",
    eyebrow: "Single-screen",
    description: "Score each of the five forces 1–5 with notes. Output an industry attractiveness verdict and document.",
    path: "/strategy/five-forces",
    action: "Score forces",
    Icon: Swords,
  },
  {
    id: "market-sizing",
    title: "Market Sizing (TAM / SAM / SOM)",
    eyebrow: "Top-down + bottom-up",
    description: "Side-by-side top-down and bottom-up sizing with low / expected / high sensitivity sliders.",
    path: "/strategy/market-sizing",
    action: "Size the market",
    Icon: Target,
  },
  {
    id: "two-by-two",
    title: "2×2 Prioritisation",
    eyebrow: "Customisable axes",
    description: "Plot any list of items on a customisable two-axis matrix. The consulting workhorse.",
    path: "/strategy/two-by-two",
    action: "Plot items",
    Icon: Crosshair,
  },
  {
    id: "exec-summary",
    title: "McKinsey-style Exec Summary",
    eyebrow: "Pyramid Principle",
    description: "Situation, Complication, Question, Answer, three supporting points. One screen, board-ready output.",
    path: "/strategy/exec-summary",
    action: "Draft summary",
    Icon: FileText,
  },
  {
    id: "issue-tree",
    title: "Hypothesis / Issue Tree",
    eyebrow: "Guided builder",
    description: "Root question → MECE branches → required analyses. Hypotheses-driven structuring.",
    path: "/strategy/issue-tree",
    action: "Build tree",
    Icon: GitBranch,
  },
  {
    id: "project-charter",
    title: "Project Charter",
    eyebrow: "Single-screen",
    description: "Scope, objectives, deliverables, timeline, budget, and governance. Sign-off ready.",
    path: "/strategy/project-charter",
    action: "Charter the project",
    Icon: ClipboardCheck,
  },
  {
    id: "options-memo",
    title: "Options Memo",
    eyebrow: "A vs B vs C",
    description: "Compare 2-4 options with trade-offs, costs, risks, and a single clear recommendation.",
    path: "/strategy/options-memo",
    action: "Draft memo",
    Icon: Scale,
  },
  {
    id: "framework-library",
    title: "Strategy Framework Library",
    eyebrow: "60+ frameworks · searchable",
    description: "Searchable index of canonical strategy frameworks (BCG, Ansoff, VRIO, PESTEL, Blue Ocean, Kano, JTBD, Theory of Change, RBV, Delta, Real Options, ZBB, EVA…). Per-framework structured prompts and exportable 1-pager.",
    path: "/strategy/framework-library",
    action: "Browse frameworks",
    Icon: Library,
  },
  {
    id: "bcg-matrix",
    title: "BCG Growth-Share Matrix",
    eyebrow: "Portfolio prioritisation",
    description: "Plot business units by growth and relative share. Live quadrant placement (Stars / Cows / Question marks / Dogs) with explicit invest / harvest / divest calls.",
    path: "/strategy/bcg-matrix",
    action: "Plot the portfolio",
    Icon: PieChart,
  },
  {
    id: "ansoff-matrix",
    title: "Ansoff Matrix",
    eyebrow: "Growth strategy selection",
    description: "Four growth bets — penetration, market development, product development, diversification — with risk ranking and an explicit investment split.",
    path: "/strategy/ansoff-matrix",
    action: "Pick growth bets",
    Icon: Move,
  },
  {
    id: "pestel",
    title: "PESTEL Analysis",
    eyebrow: "Macro scan",
    description: "Score Political, Economic, Social, Technological, Environmental, Legal forces with impact ratings. Surface the top 3 forces shaping the next 3 years.",
    path: "/strategy/pestel",
    action: "Run macro scan",
    Icon: Globe,
  },
  {
    id: "business-model-canvas",
    title: "Business Model Canvas",
    eyebrow: "9-block canvas",
    description: "Osterwalder canvas across segments, value, channels, relationships, revenue, resources, activities, partners, cost — plus a riskiest-assumption call-out.",
    path: "/strategy/business-model-canvas",
    action: "Build canvas",
    Icon: Layers,
  },
  {
    id: "vrio",
    title: "VRIO Framework",
    eyebrow: "Resource audit",
    description: "Tick Valuable / Rare / costly to Imitate / Organised across resources. Live verdicts: parity, temporary, unused, or sustained competitive advantage.",
    path: "/strategy/vrio",
    action: "Audit resources",
    Icon: ShieldCheck,
  },
  {
    id: "rca",
    title: "5 Whys + Fishbone Root Cause",
    eyebrow: "Diagnosis",
    description: "Cascade through five whys, optionally widen with a six-category fishbone (people / process / tech / materials / environment / measurement), commit to a single owned countermeasure.",
    path: "/strategy/rca",
    action: "Diagnose root cause",
    Icon: Network,
  },
  {
    id: "ir35-cross-sell",
    title: "IR35 Determinator",
    eyebrow: "Cross-sell · existing tool",
    description: "Independent strategy consultants: pressure-test the engagement against CEST-style factors.",
    path: "/product/ir35",
    action: "Open IR35 tool",
    Icon: Briefcase,
  },
  {
    id: "day-rate-cross-sell",
    title: "Day-Rate Comparator",
    eyebrow: "Cross-sell · existing tool",
    description: "Compare net take-home as Ltd, umbrella, or PAYE for consultancy day rates.",
    path: "/product/day-rate",
    action: "Compare rates",
    Icon: Calculator,
  },
  {
    id: "consulting-contract-cross-sell",
    title: "Consulting Contract",
    eyebrow: "Cross-sell · existing tool",
    description: "Generate the engagement contract with PDF and Word export.",
    path: "/startup/consulting-client-contract",
    action: "Open contract",
    Icon: Handshake,
  },
];

const newBullet = (text) => ({ id: newId("b"), text });

export const defaultSwot = () => ({
  title: "Northstar Studio — Q3 strategic review",
  context: "Position Northstar against incumbents and identify the next 90-day priorities.",
  date: new Date().toISOString().slice(0, 10),
  strengths: [newBullet("UK-native templates and compliance"), newBullet("Lean two-person team — fast iteration"), newBullet("Existing 1,200 weekly active users")],
  weaknesses: [newBullet("Limited brand awareness outside founder circles"), newBullet("Reliant on founder-led sales"), newBullet("No native mobile app")],
  opportunities: [newBullet("Open Banking + OCR upsell to existing base"), newBullet("Accountancy practice partnerships"), newBullet("UK GDPR rebrand of legal templates")],
  threats: [newBullet("Generic global template marketplaces dropping prices"), newBullet("AI tools commoditising document drafting"), newBullet("HMRC rule changes obsoleting tax math")],
});

export const fiveForces = [
  { id: "rivalry", label: "Competitive rivalry", question: "How intense is competition between existing players?" },
  { id: "newEntrants", label: "Threat of new entrants", question: "How easy is it for new players to enter and disrupt?" },
  { id: "substitutes", label: "Threat of substitutes", question: "How easy is it for customers to switch to alternative solutions?" },
  { id: "buyerPower", label: "Buyer power", question: "How much power do customers have to drive prices down?" },
  { id: "supplierPower", label: "Supplier power", question: "How much leverage do suppliers have over your costs?" },
];

export const defaultFiveForces = () => ({
  industry: "UK SME planning + admin software",
  scope: "Solo founders and freelancers in the UK earning £20-£250k/year.",
  date: new Date().toISOString().slice(0, 10),
  scores: {
    rivalry: { score: 3, note: "Several global template marketplaces and a handful of UK SaaS competitors. Differentiation is possible but fragile." },
    newEntrants: { score: 3, note: "Low technical barriers; brand and UK specificity are the moats." },
    substitutes: { score: 4, note: "Word docs + Google + accountants will always be plausible substitutes." },
    buyerPower: { score: 2, note: "Low switching cost but small per-customer ACV — buyers have limited collective leverage." },
    supplierPower: { score: 2, note: "Cloud + payment + email vendors are commodity; low single-supplier risk." },
  },
});

export const defaultMarketSizing = () => ({
  product: "UK Freelancer Toolkit",
  region: "United Kingdom",
  date: new Date().toISOString().slice(0, 10),
  topDownIndustryGbp: 820000000,
  topDownAddressablePct: 30,
  topDownServiceablePct: 35,
  topDownObtainablePct: 5,
  bottomUpCustomers: 4500000,
  bottomUpAddressablePct: 12,
  bottomUpArpuGbp: 180,
  bottomUpServeRatePct: 30,
  bottomUpObtainPct: 4,
  sensitivity: { low: 0.7, expected: 1, high: 1.3 },
});

export const defaultTwoByTwo = () => ({
  title: "Initiative prioritisation",
  xAxisLabel: "Effort",
  xLow: "Low effort",
  xHigh: "High effort",
  yAxisLabel: "Impact",
  yLow: "Low impact",
  yHigh: "High impact",
  quadrants: {
    "high-low": { label: "Quick wins" },
    "high-high": { label: "Big bets" },
    "low-low": { label: "Fillers" },
    "low-high": { label: "Time sinks" },
  },
  items: [
    { id: newId("itm"), label: "Onboarding wizard", x: 3, y: 8 },
    { id: newId("itm"), label: "PWA", x: 5, y: 6 },
    { id: newId("itm"), label: "Open Banking", x: 9, y: 8 },
    { id: newId("itm"), label: "Receipt OCR", x: 6, y: 7 },
    { id: newId("itm"), label: "Comparator", x: 4, y: 4 },
    { id: newId("itm"), label: "Industry landing pages", x: 2, y: 3 },
  ],
});

export const defaultExecSummary = () => ({
  title: "Path to £100k MRR by EOY",
  audience: "Northstar Studio board",
  date: new Date().toISOString().slice(0, 10),
  situation: "Northstar reached £20k MRR in 14 months with 1,200 weekly active users on a freemium model. Growth is steady but flat at 6% MoM.",
  complication: "Without a paid acquisition channel and a higher ARPU plan, the model bottlenecks at ~£35k MRR. Competitors are launching similar tools at lower price points.",
  question: "How do we reach £100k MRR within 12 months without diluting positioning or burning runway?",
  answer: "Move upmarket to small UK firms, ship two paid-only features, and open a partnership channel.",
  supports: [
    {
      id: newId("sup"),
      headline: "Move upmarket — Pro and Studio plans for 2-10 person teams",
      points: [
        "Existing user research shows 22% are 2-10 person firms paying £29 happily.",
        "Studio plan at £99 captures expansion ARR with multi-seat invoicing.",
        "Validated by 6 customer-development interviews in March.",
      ],
    },
    {
      id: newId("sup"),
      headline: "Ship Open Banking + Receipt OCR as paid-only differentiators",
      points: [
        "Open Banking + OCR sit at the top of the willingness-to-pay list in interviews.",
        "Each feature lifts ARPU by an estimated 30% based on comparable benchmarks.",
        "Both are deliverable in two quarters with current team.",
      ],
    },
    {
      id: newId("sup"),
      headline: "Open partnership channel via UK accountancy practices",
      points: [
        "20 practices already informally recommend the toolkit.",
        "Practice tier (£199-£299/mo) creates a structured referral channel.",
        "Aim for 30 partner practices and 600 referred users in 12 months.",
      ],
    },
  ],
  recommendation: "Approve the upmarket move, ring-fence two engineers for Open Banking + OCR, and hire a part-time partnerships lead in Q3.",
});

export const defaultIssueTree = () => ({
  rootQuestion: "How do we reach £100k MRR within 12 months?",
  context: "Northstar Studio strategic review.",
  date: new Date().toISOString().slice(0, 10),
  branches: [
    {
      id: newId("br"),
      label: "Acquire more users",
      analyses: [
        { id: newId("an"), label: "Audit current acquisition channels and CAC by source.", owner: "Bob" },
        { id: newId("an"), label: "Test 3 paid acquisition channels with £5k each.", owner: "Bob" },
      ],
      children: [
        { id: newId("br"), label: "Add paid acquisition", analyses: [{ id: newId("an"), label: "Run Google Ads pilot on UK invoice keywords." }] },
        { id: newId("br"), label: "Launch partner channel", analyses: [{ id: newId("an"), label: "Sign 5 accountant partners on a referral fee." }] },
      ],
    },
    {
      id: newId("br"),
      label: "Increase ARPU",
      analyses: [
        { id: newId("an"), label: "Analyse Pro vs Free conversion data and find friction points." },
      ],
      children: [
        { id: newId("br"), label: "Ship paid-only features", analyses: [{ id: newId("an"), label: "Validate Open Banking + OCR willingness-to-pay with 10 paying customers." }] },
        { id: newId("br"), label: "Introduce Studio tier", analyses: [{ id: newId("an"), label: "Price-test £99 / £149 / £199 with 3 cohorts." }] },
      ],
    },
    {
      id: newId("br"),
      label: "Reduce churn",
      analyses: [
        { id: newId("an"), label: "Build a churn cohort dashboard by plan and segment." },
      ],
      children: [
        { id: newId("br"), label: "Improve activation", analyses: [{ id: newId("an"), label: "Ship onboarding wizard to lift week-1 doc completions." }] },
      ],
    },
  ],
});

export const defaultProjectCharter = () => ({
  projectName: "Open Banking integration",
  sponsor: "Bob, Founder",
  manager: "Lead engineer",
  startDate: new Date().toISOString().slice(0, 10),
  endDate: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
  budget: "£28,000",
  goal: "Connect users' UK bank accounts to auto-populate the tax estimator and invoice tools.",
  scopeIn: "TrueLayer integration with 6 biggest UK banks; categorisation engine; consent + revoke flow; daily refresh; reporting screen.",
  scopeOut: "MTD submission; reconciliation with third-party accounting software; multi-currency.",
  objectives: "40% of paid users connect a bank within 14 days. Tax estimate accuracy ±2% on a 30-account benchmark. Zero P1 security incidents.",
  deliverables: "Connect/disconnect UI; daily transaction sync worker; categorisation rules engine; consent screen; observability dashboard.",
  milestones: "Week 2: TrueLayer sandbox connected. Week 6: First categorised transactions in production. Week 10: Beta with 30 users. Week 12: GA.",
  risks: "FCA compliance overhead. PSD2 90-day re-auth user friction. Vendor cost overshoot.",
  governance: "Weekly steering with sponsor + lead engineer + designer. Major scope changes require sponsor approval.",
  successCriteria: "Tax accuracy ±2%; 40% paid-user connect rate; <0.1% security incidents; positive NPS lift on paid.",
});

export const defaultOptionsMemo = () => ({
  title: "Choosing the next major investment",
  context: "We have one engineering team-quarter to invest in either Open Banking, Receipt OCR, or doubling down on the wizard. Pick one and articulate trade-offs.",
  decisionOwner: "Bob",
  decisionDate: new Date().toISOString().slice(0, 10),
  options: [
    {
      id: newId("opt"),
      label: "A. Open Banking",
      summary: "TrueLayer integration with 6 UK banks for auto-categorised transactions.",
      pros: "Highest stated willingness-to-pay; converts free → paid; unique UK angle.",
      cons: "FCA + PSD2 compliance; per-user vendor cost; ~10 weeks to GA.",
      cost: "£28,000 + ~£3 per active user / month",
      risk: "High",
      isRecommended: true,
    },
    {
      id: newId("opt"),
      label: "B. Receipt OCR",
      summary: "Photograph or upload a receipt; auto-extract supplier, date, total, VAT.",
      pros: "Solves the most-mentioned bookkeeping pain; lower compliance risk; broader appeal.",
      cons: "Per-receipt OCR cost; UK thermal-paper accuracy is uneven; less of a moat.",
      cost: "£18,000 + ~£0.04 / page",
      risk: "Medium",
      isRecommended: false,
    },
    {
      id: newId("opt"),
      label: "C. Double down on the wizard",
      summary: "Polish onboarding wizard, build personalised recommendations, run paid acquisition tests.",
      pros: "Lowest risk; lifts activation across the entire toolkit; quickest to ship.",
      cons: "No new ARPU lever; doesn't address bookkeeping pain; less differentiated.",
      cost: "£9,000",
      risk: "Low",
      isRecommended: false,
    },
  ],
  recommendation: "Pick A (Open Banking). Highest willingness-to-pay, strongest UK moat, and turns the toolkit from a document utility into a financial copilot. Mitigate the compliance risk by doing the FCA work in parallel with build.",
});
